import type { ChartOfAccount, EntryStatus, JournalEntryLine } from './accounting.schema'
import { AccountingService } from './accounting.service'

export interface TrialBalanceLine {
  code: string
  name: string
  accountType: string
  debit: number
  credit: number
  balance: number
}

export interface TrialBalanceRow {
  code: string
  name: string
  accountType: string
  debit: number
  credit: number
  balance: number
}

export interface IncomeStatementRow {
  code: string
  name: string
  amount: number
  type: 'income' | 'expense'
}

export interface MultiStepIncomeStatement {
  operatingRevenue: IncomeStatementRow[]
  totalOperatingRevenue: number
  costOfGoodsSold: IncomeStatementRow[]
  totalCostOfGoodsSold: number
  grossProfit: number
  operatingExpenses: IncomeStatementRow[]
  totalOperatingExpenses: number
  operatingIncome: number
  otherIncome: IncomeStatementRow[]
  totalOtherIncome: number
  otherExpenses: IncomeStatementRow[]
  totalOtherExpenses: number
  netIncome: number
}

export interface ComparativeIncomeStatementLine {
  code: string
  name: string
  currentAmount: number
  priorAmount: number
  changeAmount: number
  changePercent: number
}

export interface ComparativeTotals {
  currentAmount: number
  priorAmount: number
  changeAmount: number
  changePercent: number
}

export interface ComparativeIncomeStatement {
  operatingRevenue: ComparativeIncomeStatementLine[]
  totalOperatingRevenue: ComparativeTotals
  costOfGoodsSold: ComparativeIncomeStatementLine[]
  totalCostOfGoodsSold: ComparativeTotals
  grossProfit: ComparativeTotals
  operatingExpenses: ComparativeIncomeStatementLine[]
  totalOperatingExpenses: ComparativeTotals
  operatingIncome: ComparativeTotals
  otherIncome: ComparativeIncomeStatementLine[]
  totalOtherIncome: ComparativeTotals
  otherExpenses: ComparativeIncomeStatementLine[]
  totalOtherExpenses: ComparativeTotals
  netIncome: ComparativeTotals
  periodLabel: string
  priorLabel: string
}

export interface BalanceSheetRow {
  code: string
  name: string
  amount: number
  category: 'asset' | 'liability' | 'equity'
  isCurrent?: boolean
}

export interface ClassifiedBalanceSheet {
  currentAssets: BalanceSheetRow[]
  totalCurrentAssets: number
  nonCurrentAssets: BalanceSheetRow[]
  totalNonCurrentAssets: number
  totalAssets: number

  currentLiabilities: BalanceSheetRow[]
  totalCurrentLiabilities: number
  nonCurrentLiabilities: BalanceSheetRow[]
  totalNonCurrentLiabilities: number
  totalLiabilities: number

  equity: BalanceSheetRow[]
  totalEquity: number

  totalLiabilitiesAndEquity: number
  isBalanced: boolean
  imbalance: number
}

export interface ConsolidatedBalanceSheet {
  consolidatedAssets: BalanceSheetRow[]
  totalConsolidatedAssets: number
  consolidatedLiabilities: BalanceSheetRow[]
  totalConsolidatedLiabilities: number
  consolidatedEquity: BalanceSheetRow[]
  totalConsolidatedEquity: number
  totalLiabilitiesAndEquity: number
  eliminations: Array<{ accountCode: string; amount: number }>
}

export interface ConsolidatedIncomeStatement {
  consolidatedRevenue: IncomeStatementRow[]
  totalConsolidatedRevenue: number
  consolidatedExpenses: IncomeStatementRow[]
  totalConsolidatedExpenses: number
  consolidatedNetIncome: number
  eliminations: Array<{ accountCode: string; amount: number }>
}

export interface StatementOfCashFlows { imbalance: number
}

export interface ComparativeBalanceSheetRow {
  code: string
  name: string
  category: 'asset' | 'liability' | 'equity'
  isCurrent?: boolean
  currentAmount: number
  priorAmount: number
  changeAmount: number
  changePercent: number
}

export interface ComparativeBalanceSheet {
  currentAssets: ComparativeBalanceSheetRow[]
  totalCurrentAssets: ComparativeTotals
  nonCurrentAssets: ComparativeBalanceSheetRow[]
  totalNonCurrentAssets: ComparativeTotals
  totalAssets: ComparativeTotals

  currentLiabilities: ComparativeBalanceSheetRow[]
  totalCurrentLiabilities: ComparativeTotals
  nonCurrentLiabilities: ComparativeBalanceSheetRow[]
  totalNonCurrentLiabilities: ComparativeTotals
  totalLiabilities: ComparativeTotals

  equity: ComparativeBalanceSheetRow[]
  totalEquity: ComparativeTotals

  totalLiabilitiesAndEquity: ComparativeTotals
}

export interface BudgetVarianceLine {
  accountCode: string
  name: string
  budget: number
  actual: number
  variance: number
  variancePercent: number
  favorable: boolean
}

export interface CashFlowStatement {
  operatingActivities: Array<{ description: string; amount: number }>
  totalOperatingCashFlow: number
  investingActivities: Array<{ description: string; amount: number }>
  totalInvestingCashFlow: number
  financingActivities: Array<{ description: string; amount: number }>
  totalFinancingCashFlow: number
  netCashFlow: number
  beginningCash: number
  endingCash: number
}

/**
 * An immutable posting boundary supplied by an application service such as
 * LedgerSync. Reports deliberately depend on accounting concepts only, so the
 * accounting bounded context remains independent from LedgerSync's schemas.
 */
export interface FinancialStatementPosting {
  tenantId: string
  entryDate: number
  status?: EntryStatus
  isPosted?: boolean
  lines: ReadonlyArray<JournalEntryLine>
}

export interface FinancialStatementPeriod {
  start: number
  end: number
}

export interface InternalFinancialStatements {
  tenantId: string
  period: FinancialStatementPeriod
  includedPostingCount: number
  includedLineCount: number
  trialBalance: TrialBalanceRow[]
  incomeStatement: MultiStepIncomeStatement
  balanceSheet: ClassifiedBalanceSheet
  cashFlowStatement: CashFlowStatement
}

export class ReportGenerator {
  /**
   * Produces the internal financial statements from finalized postings.
   *
   * The application layer is responsible for translating its own commands
   * (for example, LedgerSync imports) into accounting postings. This boundary
   * keeps draft, voided, out-of-period, and cross-tenant activity out of the
   * financial statements without coupling this domain package to a workflow.
   */
  static generateInternalFinancialStatements(
    accounts: ChartOfAccount[],
    postings: ReadonlyArray<FinancialStatementPosting>,
    tenantId: string,
    period: FinancialStatementPeriod,
    beginningCash: number = 0,
  ): InternalFinancialStatements {
    if (period.start > period.end) {
      throw new RangeError('Financial statement period start must not be after its end')
    }

    const includedPostings = postings.filter(posting =>
      posting.tenantId === tenantId &&
      (posting.status === 'posted' || posting.isPosted === true) &&
      posting.entryDate >= period.start &&
      posting.entryDate <= period.end,
    )
    const includedLines = includedPostings.flatMap(posting => posting.lines)
    const tenantAccounts = accounts.filter(account => account.tenantId === tenantId)
    const trialBalance = this.generateTrialBalance(tenantAccounts, includedLines)

    return {
      tenantId,
      period,
      includedPostingCount: includedPostings.length,
      includedLineCount: includedLines.length,
      trialBalance,
      incomeStatement: this.generateMultiStepIncomeStatement(tenantAccounts, includedLines),
      balanceSheet: this.generateClassifiedBalanceSheet(tenantAccounts, includedLines),
      cashFlowStatement: this.generateCashFlowStatement(tenantAccounts, includedLines, beginningCash),
    }
  }

  /**
   * Generate a trial balance from journal entry lines.
   */
  static generateTrialBalance(
    accounts: ChartOfAccount[],
    lines: JournalEntryLine[],
  ): TrialBalanceRow[] {
    return AccountingService.computeTrialBalance(accounts, lines).map(r => {
      const account = accounts.find(a => a.code === r.code)
      return {
        ...r,
        accountType: account?.accountType ?? '',
      }
    })
  }

  /**
   * Generate Multi-Step Income Statement (GAAP / CDA format).
   */
  static generateMultiStepIncomeStatement(
    accounts: ChartOfAccount[],
    lines: JournalEntryLine[],
    period?: { start: number; end: number }
  ): MultiStepIncomeStatement {
    const filtered = period
      ? lines.filter(l => {
          const entry = (l as any)._entryDate || (l as any).entryDate
          return !entry || (entry >= period.start && entry <= period.end)
        })
      : lines

    const trialBalance = this.generateTrialBalance(accounts, filtered)

    const operatingRevenue: IncomeStatementRow[] = []
    const otherIncome: IncomeStatementRow[] = []
    const costOfGoodsSold: IncomeStatementRow[] = []
    const operatingExpenses: IncomeStatementRow[] = []
    const otherExpenses: IncomeStatementRow[] = []

    for (const r of trialBalance) {
      if (r.balance === 0 && r.debit === 0 && r.credit === 0) continue

      const acc = accounts.find(a => a.code === r.code)
      const subtype = acc?.subtype

      if (r.accountType === 'income') {
        if (subtype === 'other_income') {
          otherIncome.push({ code: r.code, name: r.name, amount: r.balance, type: 'income' })
        } else {
          operatingRevenue.push({ code: r.code, name: r.name, amount: r.balance, type: 'income' })
        }
      } else if (r.accountType === 'expense') {
        if (subtype === 'cost_of_goods_sold' || subtype === 'direct_labor') {
          costOfGoodsSold.push({ code: r.code, name: r.name, amount: r.balance, type: 'expense' })
        } else if (subtype === 'interest_expense' || subtype === 'bank_service_charges' || subtype === 'other_expense') {
          otherExpenses.push({ code: r.code, name: r.name, amount: r.balance, type: 'expense' })
        } else {
          operatingExpenses.push({ code: r.code, name: r.name, amount: r.balance, type: 'expense' })
        }
      }
    }

    const sum = (arr: IncomeStatementRow[]) => Math.round(arr.reduce((s, r) => s + r.amount, 0) * 100) / 100

    const totalOperatingRevenue = sum(operatingRevenue)
    const totalCostOfGoodsSold = sum(costOfGoodsSold)
    const grossProfit = Math.round((totalOperatingRevenue - totalCostOfGoodsSold) * 100) / 100
    const totalOperatingExpenses = sum(operatingExpenses)
    const operatingIncome = Math.round((grossProfit - totalOperatingExpenses) * 100) / 100
    const totalOtherIncome = sum(otherIncome)
    const totalOtherExpenses = sum(otherExpenses)
    const netIncome = Math.round((operatingIncome + totalOtherIncome - totalOtherExpenses) * 100) / 100

    return {
      operatingRevenue,
      totalOperatingRevenue,
      costOfGoodsSold,
      totalCostOfGoodsSold,
      grossProfit,
      operatingExpenses,
      totalOperatingExpenses,
      operatingIncome,
      otherIncome,
      totalOtherIncome,
      otherExpenses,
      totalOtherExpenses,
      netIncome,
    }
  }

  /**
   * Backwards compatible simple Income Statement.
   */
  static generateIncomeStatement(
    accounts: ChartOfAccount[],
    lines: JournalEntryLine[],
    period?: { start: number; end: number },
  ): { revenue: IncomeStatementRow[]; expenses: IncomeStatementRow[]; netSurplus: number } {
    const multi = this.generateMultiStepIncomeStatement(accounts, lines, period)
    const revenue = [...multi.operatingRevenue, ...multi.otherIncome]
    const expenses = [...multi.costOfGoodsSold, ...multi.operatingExpenses, ...multi.otherExpenses]
    return {
      revenue,
      expenses,
      netSurplus: multi.netIncome,
    }
  }

  /**
   * Generate Classified Balance Sheet with balance verification.
   */
  static generateClassifiedBalanceSheet(
    accounts: ChartOfAccount[],
    lines: JournalEntryLine[],
  ): ClassifiedBalanceSheet {
    const trialBalance = this.generateTrialBalance(accounts, lines)

    const currentAssets: BalanceSheetRow[] = []
    const nonCurrentAssets: BalanceSheetRow[] = []
    const currentLiabilities: BalanceSheetRow[] = []
    const nonCurrentLiabilities: BalanceSheetRow[] = []
    const equity: BalanceSheetRow[] = []

    for (const r of trialBalance) {
      if (r.balance === 0 && r.debit === 0 && r.credit === 0) continue

      const acc = accounts.find(a => a.code === r.code)
      const subtype = acc?.subtype
      const amount = Math.abs(r.balance)

      if (r.accountType === 'asset') {
        const isNonCurrent = subtype === 'fixed_asset' || subtype === 'accumulated_depreciation' || subtype === 'other_non_current_asset' || r.code.startsWith('15') || r.code.startsWith('16')
        if (isNonCurrent) {
          nonCurrentAssets.push({ code: r.code, name: r.name, amount: r.balance, category: 'asset', isCurrent: false })
        } else {
          currentAssets.push({ code: r.code, name: r.name, amount: r.balance, category: 'asset', isCurrent: true })
        }
      } else if (r.accountType === 'liability') {
        const isNonCurrent = subtype === 'long_term_debt' || subtype === 'other_non_current_liability' || r.code.startsWith('25')
        if (isNonCurrent) {
          nonCurrentLiabilities.push({ code: r.code, name: r.name, amount: r.balance, category: 'liability', isCurrent: false })
        } else {
          currentLiabilities.push({ code: r.code, name: r.name, amount: r.balance, category: 'liability', isCurrent: true })
        }
      } else if (r.accountType === 'equity') {
        equity.push({ code: r.code, name: r.name, amount: r.balance, category: 'equity' })
      }
    }

    // Include Net Income from operations into Retained Earnings / Current Period Surplus
    const incomeStmt = this.generateIncomeStatement(accounts, lines)
    if (incomeStmt.netSurplus !== 0) {
      equity.push({
        code: 'NET_INCOME',
        name: 'Current Period Net Surplus / (Deficit)',
        amount: incomeStmt.netSurplus,
        category: 'equity',
      })
    }

    const sum = (arr: BalanceSheetRow[]) => Math.round(arr.reduce((s, r) => s + r.amount, 0) * 100) / 100

    const totalCurrentAssets = sum(currentAssets)
    const totalNonCurrentAssets = sum(nonCurrentAssets)
    const totalAssets = Math.round((totalCurrentAssets + totalNonCurrentAssets) * 100) / 100

    const totalCurrentLiabilities = sum(currentLiabilities)
    const totalNonCurrentLiabilities = sum(nonCurrentLiabilities)
    const totalLiabilities = Math.round((totalCurrentLiabilities + totalNonCurrentLiabilities) * 100) / 100

    const totalEquity = sum(equity)
    const totalLiabilitiesAndEquity = Math.round((totalLiabilities + totalEquity) * 100) / 100

    const imbalance = Math.round(Math.abs(totalAssets - totalLiabilitiesAndEquity) * 100) / 100
    const isBalanced = imbalance < 0.01

    return {
      currentAssets,
      totalCurrentAssets,
      nonCurrentAssets,
      totalNonCurrentAssets,
      totalAssets,
      currentLiabilities,
      totalCurrentLiabilities,
      nonCurrentLiabilities,
      totalNonCurrentLiabilities,
      totalLiabilities,
      equity,
      totalEquity,
      totalLiabilitiesAndEquity,
      isBalanced,
      imbalance,
    }
  }

  /**
   * Backwards compatible simple Balance Sheet.
   */
  static generateBalanceSheet(
    accounts: ChartOfAccount[],
    lines: JournalEntryLine[],
  ): { assets: BalanceSheetRow[]; liabilities: BalanceSheetRow[]; equity: BalanceSheetRow[] } {
    const classified = this.generateClassifiedBalanceSheet(accounts, lines)
    return {
      assets: [...classified.currentAssets, ...classified.nonCurrentAssets],
      liabilities: [...classified.currentLiabilities, ...classified.nonCurrentLiabilities],
      equity: classified.equity,
    }
  }

  /**
   * Generate Statement of Cash Flows.
   */
  static generateCashFlowStatement(
    accounts: ChartOfAccount[],
    lines: JournalEntryLine[],
    beginningCash: number = 0
  ): CashFlowStatement {
    const trialBalance = this.generateTrialBalance(accounts, lines)
    const incomeStmt = this.generateIncomeStatement(accounts, lines)

    const operatingActivities: Array<{ description: string; amount: number }> = [
      { description: 'Net Income / Surplus for the Period', amount: incomeStmt.netSurplus },
    ]

    const investingActivities: Array<{ description: string; amount: number }> = []
    const financingActivities: Array<{ description: string; amount: number }> = []

    let cashEndingBalance = 0

    for (const r of trialBalance) {
      const acc = accounts.find(a => a.code === r.code)
      if (acc?.subtype === 'cash_and_bank' || r.code.startsWith('10')) {
        cashEndingBalance += r.balance
      } else if (acc?.subtype === 'fixed_asset' || r.code.startsWith('15')) {
        if (r.balance !== 0) {
          investingActivities.push({ description: `Capital Expenditures (${r.name})`, amount: -Math.abs(r.balance) })
        }
      } else if (acc?.accountType === 'equity' || acc?.subtype === 'long_term_debt') {
        if (r.balance !== 0) {
          financingActivities.push({ description: `Financing / Equity Contribution (${r.name})`, amount: r.balance })
        }
      }
    }

    const sum = (arr: Array<{ amount: number }>) => Math.round(arr.reduce((s, r) => s + r.amount, 0) * 100) / 100

    const totalOperatingCashFlow = sum(operatingActivities)
    const totalInvestingCashFlow = sum(investingActivities)
    const totalFinancingCashFlow = sum(financingActivities)
    const netCashFlow = Math.round((totalOperatingCashFlow + totalInvestingCashFlow + totalFinancingCashFlow) * 100) / 100

    return {
      operatingActivities,
      totalOperatingCashFlow,
      investingActivities,
      totalInvestingCashFlow,
      financingActivities,
      totalFinancingCashFlow,
      netCashFlow,
      beginningCash: Math.round(beginningCash * 100) / 100,
      endingCash: Math.round((beginningCash + netCashFlow) * 100) / 100,
    }
  }

  /**
   * CDA-mandated statutory fund allocation computation.
   */
  static computeStatutoryAllocations(netSurplus: number): Array<{ fundType: string; percentage: number; amount: number }> {
    return [
      { fundType: 'Reserve Fund', percentage: 10, amount: Math.round(netSurplus * 0.10 * 100) / 100 },
      { fundType: 'Education and Training Fund', percentage: 10, amount: Math.round(netSurplus * 0.10 * 100) / 100 },
      { fundType: 'Community Development Fund', percentage: 3, amount: Math.round(netSurplus * 0.03 * 100) / 100 },
      { fundType: 'Optional Fund', percentage: 7, amount: Math.round(netSurplus * 0.07 * 100) / 100 },
    ]
  }

  /**
   * Compute patronage refund and interest on capital.
   */
  static computeBenefitDistribution(
    netSurplus: number,
    totalStatutoryFunds: number,
    memberTransactionVolume: number,
    totalTransactionVolume: number,
    totalShareCapital: number,
  ): { patronageRefundPool: number; patronageRefundRate: number; interestOnCapitalPool: number; interestRate: number } {
    const distributable = netSurplus - totalStatutoryFunds
    const patronagePool = distributable * 0.70 // 70% for patronage refund
    const interestPool = distributable * 0.30  // 30% for interest on capital

    return {
      patronageRefundPool: Math.round(patronagePool * 100) / 100,
      patronageRefundRate: totalTransactionVolume > 0 ? patronagePool / totalTransactionVolume : 0,
      interestOnCapitalPool: Math.round(interestPool * 100) / 100,
      interestRate: totalShareCapital > 0 ? (interestPool / totalShareCapital) * 100 : 0,
    }
  }

  static generateComparativeIncomeStatement(
    currentAccounts: ChartOfAccount[],
    currentLines: JournalEntryLine[],
    priorAccounts: ChartOfAccount[],
    priorLines: JournalEntryLine[],
    periodLabel: string = 'Current',
    priorLabel: string = 'Prior'
  ): ComparativeIncomeStatement {
    const current = this.generateMultiStepIncomeStatement(currentAccounts, currentLines)
    const prior = this.generateMultiStepIncomeStatement(priorAccounts, priorLines)

    const calcChange = (curr: number, pri: number) => {
      const changeAmount = Math.round((curr - pri) * 100) / 100
      let changePercent = 0
      if (pri !== 0) changePercent = Math.round((changeAmount / Math.abs(pri)) * 10000) / 100
      return { changeAmount, changePercent }
    }

    const mergeLines = (currArr: IncomeStatementRow[], priArr: IncomeStatementRow[]): ComparativeIncomeStatementLine[] => {
      const map = new Map<string, ComparativeIncomeStatementLine>()
      const add = (r: IncomeStatementRow, isCurrent: boolean) => {
        if (!map.has(r.code)) {
          map.set(r.code, {
            code: r.code, name: r.name, currentAmount: 0, priorAmount: 0, changeAmount: 0, changePercent: 0
          })
        }
        const item = map.get(r.code)!
        if (isCurrent) item.currentAmount = r.amount
        else item.priorAmount = r.amount
      }
      currArr.forEach(r => add(r, true))
      priArr.forEach(r => add(r, false))
      
      const res = Array.from(map.values()).map(item => {
        const { changeAmount, changePercent } = calcChange(item.currentAmount, item.priorAmount)
        return { ...item, changeAmount, changePercent }
      })
      res.sort((a, b) => a.code.localeCompare(b.code))
      return res
    }

    const calcTotal = (curr: number, pri: number): ComparativeTotals => {
      const { changeAmount, changePercent } = calcChange(curr, pri)
      return { currentAmount: curr, priorAmount: pri, changeAmount, changePercent }
    }

    return {
      periodLabel,
      priorLabel,
      operatingRevenue: mergeLines(current.operatingRevenue, prior.operatingRevenue),
      totalOperatingRevenue: calcTotal(current.totalOperatingRevenue, prior.totalOperatingRevenue),
      costOfGoodsSold: mergeLines(current.costOfGoodsSold, prior.costOfGoodsSold),
      totalCostOfGoodsSold: calcTotal(current.totalCostOfGoodsSold, prior.totalCostOfGoodsSold),
      grossProfit: calcTotal(current.grossProfit, prior.grossProfit),
      operatingExpenses: mergeLines(current.operatingExpenses, prior.operatingExpenses),
      totalOperatingExpenses: calcTotal(current.totalOperatingExpenses, prior.totalOperatingExpenses),
      operatingIncome: calcTotal(current.operatingIncome, prior.operatingIncome),
      otherIncome: mergeLines(current.otherIncome, prior.otherIncome),
      totalOtherIncome: calcTotal(current.totalOtherIncome, prior.totalOtherIncome),
      otherExpenses: mergeLines(current.otherExpenses, prior.otherExpenses),
      totalOtherExpenses: calcTotal(current.totalOtherExpenses, prior.totalOtherExpenses),
      netIncome: calcTotal(current.netIncome, prior.netIncome)
    }
  }

  static generateComparativeBalanceSheet(
    currentAccounts: ChartOfAccount[],
    currentLines: JournalEntryLine[],
    priorAccounts: ChartOfAccount[],
    priorLines: JournalEntryLine[]
  ): ComparativeBalanceSheet {
    const current = this.generateClassifiedBalanceSheet(currentAccounts, currentLines)
    const prior = this.generateClassifiedBalanceSheet(priorAccounts, priorLines)

    const calcChange = (curr: number, pri: number) => {
      const changeAmount = Math.round((curr - pri) * 100) / 100
      let changePercent = 0
      if (pri !== 0) changePercent = Math.round((changeAmount / Math.abs(pri)) * 10000) / 100
      return { changeAmount, changePercent }
    }

    const mergeLines = (currArr: BalanceSheetRow[], priArr: BalanceSheetRow[]): ComparativeBalanceSheetRow[] => {
      const map = new Map<string, ComparativeBalanceSheetRow>()
      const add = (r: BalanceSheetRow, isCurrent: boolean) => {
        if (!map.has(r.code)) {
          map.set(r.code, {
            code: r.code, name: r.name, category: r.category, isCurrent: r.isCurrent,
            currentAmount: 0, priorAmount: 0, changeAmount: 0, changePercent: 0
          })
        }
        const item = map.get(r.code)!
        if (isCurrent) item.currentAmount = r.amount
        else item.priorAmount = r.amount
      }
      currArr.forEach(r => add(r, true))
      priArr.forEach(r => add(r, false))
      
      const res = Array.from(map.values()).map(item => {
        const { changeAmount, changePercent } = calcChange(item.currentAmount, item.priorAmount)
        return { ...item, changeAmount, changePercent }
      })
      res.sort((a, b) => a.code.localeCompare(b.code))
      return res
    }

    const calcTotal = (curr: number, pri: number): ComparativeTotals => {
      const { changeAmount, changePercent } = calcChange(curr, pri)
      return { currentAmount: curr, priorAmount: pri, changeAmount, changePercent }
    }

    return {
      currentAssets: mergeLines(current.currentAssets, prior.currentAssets),
      totalCurrentAssets: calcTotal(current.totalCurrentAssets, prior.totalCurrentAssets),
      nonCurrentAssets: mergeLines(current.nonCurrentAssets, prior.nonCurrentAssets),
      totalNonCurrentAssets: calcTotal(current.totalNonCurrentAssets, prior.totalNonCurrentAssets),
      totalAssets: calcTotal(current.totalAssets, prior.totalAssets),
      currentLiabilities: mergeLines(current.currentLiabilities, prior.currentLiabilities),
      totalCurrentLiabilities: calcTotal(current.totalCurrentLiabilities, prior.totalCurrentLiabilities),
      nonCurrentLiabilities: mergeLines(current.nonCurrentLiabilities, prior.nonCurrentLiabilities),
      totalNonCurrentLiabilities: calcTotal(current.totalNonCurrentLiabilities, prior.totalNonCurrentLiabilities),
      totalLiabilities: calcTotal(current.totalLiabilities, prior.totalLiabilities),
      equity: mergeLines(current.equity, prior.equity),
      totalEquity: calcTotal(current.totalEquity, prior.totalEquity),
      totalLiabilitiesAndEquity: calcTotal(current.totalLiabilitiesAndEquity, prior.totalLiabilitiesAndEquity)
    }
  }

  static generateBudgetVsActual(
    budgetLines: { accountCode: string; budgetAmount: number }[],
    actualAccounts: ChartOfAccount[],
    actualLines: JournalEntryLine[]
  ): BudgetVarianceLine[] {
    const trialBalance = this.generateTrialBalance(actualAccounts, actualLines)
    const map = new Map<string, BudgetVarianceLine>()

    for (const b of budgetLines) {
      const account = actualAccounts.find(a => a.code === b.accountCode)
      map.set(b.accountCode, {
        accountCode: b.accountCode,
        name: account?.name || 'Unknown Account',
        budget: b.budgetAmount,
        actual: 0,
        variance: 0,
        variancePercent: 0,
        favorable: false
      })
    }

    for (const r of trialBalance) {
      if (r.balance === 0 && r.debit === 0 && r.credit === 0) continue
      const account = actualAccounts.find(a => a.code === r.code)
      if (!account) continue

      if (!map.has(r.code)) {
        map.set(r.code, {
          accountCode: r.code,
          name: r.name,
          budget: 0,
          actual: 0,
          variance: 0,
          variancePercent: 0,
          favorable: false
        })
      }
      
      const item = map.get(r.code)!
      item.actual = r.balance
    }

    const res = Array.from(map.values()).map(item => {
      const account = actualAccounts.find(a => a.code === item.accountCode)
      const isRevenue = account?.accountType === 'income' || account?.accountType === 'equity' || account?.accountType === 'liability'
      
      let variance = 0
      let favorable = false
      if (isRevenue) {
        variance = item.actual - item.budget
        favorable = variance >= 0
      } else {
        variance = item.budget - item.actual
        favorable = variance >= 0
      }
      
      variance = Math.round(variance * 100) / 100
      
      let variancePercent = 0
      if (item.budget !== 0) {
        variancePercent = Math.round((variance / Math.abs(item.budget)) * 10000) / 100
      } else if (item.actual !== 0) {
        variancePercent = favorable ? 100 : -100
      }

      return { ...item, variance, variancePercent, favorable }
    })

    res.sort((a, b) => a.accountCode.localeCompare(b.accountCode))
    return res
  }

  /**
   * Generate Multi-Step Income Statement (P&L) filtered by Cost Center.
   */
  static generateCostCenterPnl(
    accounts: ChartOfAccount[],
    lines: JournalEntryLine[],
    costCenterId: string,
    period?: { start: number; end: number }
  ): MultiStepIncomeStatement {
    const filteredLines = lines.filter(l => l.costCenterId === costCenterId)
    return this.generateMultiStepIncomeStatement(accounts, filteredLines, period)
  }

  static generateJobCostingReport(
    records: import('./accounting.schema').JobCostRecord[],
    budgetAmount: number,
    projectRevenue: number
  ) {
    let totalMaterial = 0
    let totalLabor = 0
    let totalSubcontractor = 0
    let totalOverhead = 0

    for (const record of records) {
      if (record.costCategory === 'Materials') totalMaterial += record.amount
      else if (record.costCategory === 'Labor') totalLabor += record.amount
      else if (record.costCategory === 'Subcontractor') totalSubcontractor += record.amount
      else if (record.costCategory === 'Overhead') totalOverhead += record.amount
    }

    const totalActualCost = totalMaterial + totalLabor + totalSubcontractor + totalOverhead
    const variance = budgetAmount - totalActualCost
    const grossProfit = projectRevenue - totalActualCost
    const grossProfitMarginPercent = projectRevenue > 0 ? (grossProfit / projectRevenue) * 100 : 0

    return {
      totalMaterial,
      totalLabor,
      totalSubcontractor,
      totalOverhead,
      totalActualCost,
      budgetAmount,
      variance,
      projectRevenue,
      grossProfit,
      grossProfitMarginPercent
    }
  }

  // ─── Consolidated Financials ──────────────────────────────────────────

  static generateConsolidatedBalanceSheet(
    tenantsData: Array<{ tenantId: string; accounts: ChartOfAccount[]; lines: JournalEntryLine[] }>,
    eliminationAccounts: { dueFromCodes: string[]; dueToCodes: string[] }
  ): ConsolidatedBalanceSheet {
    const combinedBalances = new Map<string, { code: string; name: string; type: string; category: 'asset' | 'liability' | 'equity'; amount: number }>()

    for (const tenant of tenantsData) {
      const tb = AccountingService.computeTrialBalance(tenant.accounts, tenant.lines)
      for (const row of tb) {
        const account = tenant.accounts.find(a => a.code === row.code)
        if (!account) continue

        let amount = row.balance
        // Keep asset balances positive for assets, liability/equity balances positive for them
        if (account.accountType === 'asset') {
          // balance is already debit - credit
        } else if (account.accountType === 'liability' || account.accountType === 'equity') {
          amount = row.balance // credit - debit
        } else {
          continue
        }

        if (!combinedBalances.has(account.code)) {
          combinedBalances.set(account.code, {
            code: account.code,
            name: account.name,
            type: account.accountType,
            category: account.accountType as 'asset' | 'liability' | 'equity',
            amount: 0,
          })
        }
        combinedBalances.get(account.code)!.amount += amount
      }
    }

    const eliminations: Array<{ accountCode: string; amount: number }> = []

    // Apply eliminations
    for (const code of eliminationAccounts.dueFromCodes) {
      if (combinedBalances.has(code)) {
        const amount = combinedBalances.get(code)!.amount
        if (amount > 0) {
          eliminations.push({ accountCode: code, amount: -amount })
          combinedBalances.get(code)!.amount = 0
        }
      }
    }
    for (const code of eliminationAccounts.dueToCodes) {
      if (combinedBalances.has(code)) {
        const amount = combinedBalances.get(code)!.amount
        if (amount > 0) {
          eliminations.push({ accountCode: code, amount: -amount })
          combinedBalances.get(code)!.amount = 0
        }
      }
    }

    const consolidatedAssets: BalanceSheetRow[] = []
    let totalConsolidatedAssets = 0
    const consolidatedLiabilities: BalanceSheetRow[] = []
    let totalConsolidatedLiabilities = 0
    const consolidatedEquity: BalanceSheetRow[] = []
    let totalConsolidatedEquity = 0

    for (const item of combinedBalances.values()) {
      if (Math.abs(item.amount) < 0.01) continue
      if (item.category === 'asset') {
        consolidatedAssets.push(item)
        totalConsolidatedAssets += item.amount
      } else if (item.category === 'liability') {
        consolidatedLiabilities.push(item)
        totalConsolidatedLiabilities += item.amount
      } else if (item.category === 'equity') {
        consolidatedEquity.push(item)
        totalConsolidatedEquity += item.amount
      }
    }

    return {
      consolidatedAssets,
      totalConsolidatedAssets,
      consolidatedLiabilities,
      totalConsolidatedLiabilities,
      consolidatedEquity,
      totalConsolidatedEquity,
      totalLiabilitiesAndEquity: totalConsolidatedLiabilities + totalConsolidatedEquity,
      eliminations,
    }
  }

  static generateConsolidatedIncomeStatement(
    tenantsData: Array<{ tenantId: string; accounts: ChartOfAccount[]; lines: JournalEntryLine[] }>,
    intercompanyRevenueExpenseCodes: string[]
  ): ConsolidatedIncomeStatement {
    const combinedBalances = new Map<string, { code: string; name: string; type: 'income' | 'expense'; amount: number }>()

    for (const tenant of tenantsData) {
      const tb = AccountingService.computeTrialBalance(tenant.accounts, tenant.lines)
      for (const row of tb) {
        const account = tenant.accounts.find(a => a.code === row.code)
        if (!account) continue
        if (account.accountType !== 'income' && account.accountType !== 'expense') continue

        let amount = row.balance
        if (!combinedBalances.has(account.code)) {
          combinedBalances.set(account.code, {
            code: account.code,
            name: account.name,
            type: account.accountType,
            amount: 0,
          })
        }
        combinedBalances.get(account.code)!.amount += amount
      }
    }

    const eliminations: Array<{ accountCode: string; amount: number }> = []

    for (const code of intercompanyRevenueExpenseCodes) {
      if (combinedBalances.has(code)) {
        const amount = combinedBalances.get(code)!.amount
        if (Math.abs(amount) > 0) {
          eliminations.push({ accountCode: code, amount: -amount })
          combinedBalances.get(code)!.amount = 0
        }
      }
    }

    const consolidatedRevenue: IncomeStatementRow[] = []
    let totalConsolidatedRevenue = 0
    const consolidatedExpenses: IncomeStatementRow[] = []
    let totalConsolidatedExpenses = 0

    for (const item of combinedBalances.values()) {
      if (Math.abs(item.amount) < 0.01) continue
      if (item.type === 'income') {
        consolidatedRevenue.push(item)
        totalConsolidatedRevenue += item.amount
      } else if (item.type === 'expense') {
        consolidatedExpenses.push(item)
        totalConsolidatedExpenses += item.amount
      }
    }

    return {
      consolidatedRevenue,
      totalConsolidatedRevenue,
      consolidatedExpenses,
      totalConsolidatedExpenses,
      consolidatedNetIncome: totalConsolidatedRevenue - totalConsolidatedExpenses,
      eliminations,
    }
  }
}
